import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, FileText, Building2, Percent, Download } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is a GST invoice?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A GST invoice is a legal document issued by a GST-registered business in India. It must include the supplier\'s GSTIN, HSN/SAC code, taxable value, and the applicable GST rates: CGST, SGST (for intra-state), or IGST (for inter-state).',
      },
    },
    {
      '@type': 'Question',
      name: 'What details are required on a GST invoice?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A valid GST invoice must include: supplier name, address, and GSTIN; invoice number and date; recipient details (name, address, GSTIN if registered); description of goods/services; HSN/SAC code; taxable value; GST rate and amount (CGST + SGST or IGST); and the total amount payable.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I create a GST invoice online for free?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic lets you create a fully compliant GST invoice online for free. Fill in your GSTIN, HSN/SAC codes, and tax rates. The invoice calculates CGST, SGST, and IGST automatically. Download as a PDF instantly.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the difference between CGST, SGST, and IGST?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'CGST (Central GST) and SGST (State GST) are charged together on intra-state transactions, within the same state. IGST (Integrated GST) is charged on inter-state transactions, between different states. Both CGST and SGST are equal halves of the total GST rate.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I add bank account details on a GST invoice?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic lets you add your bank name, account number, IFSC code, and branch to your GST invoice so clients can make direct bank transfers.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is a GST invoice the same as a tax invoice?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. A GST invoice and a tax invoice are the same document in India. Under the GST regime, the official term is "tax invoice" and it is mandatory for all GST-registered businesses to issue one for taxable supplies.',
      },
    },
  ],
};

const fields = [
  'Supplier GSTIN and business details',
  'Buyer GSTIN (if registered)',
  'HSN / SAC code per line item',
  'Auto-calculated CGST + SGST (intra-state)',
  'Auto-calculated IGST (inter-state)',
  'Taxable value, GST amount, and total',
  'Bank account details for payment',
  'Invoice number, date, and place of supply',
];

export default function GstInvoiceGeneratorPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="GST Invoice Generator: Free Online GST Invoice Maker | Invilic"
        description="Create GST-compliant invoices online free. Auto-calculates CGST, SGST & IGST. Add GSTIN, HSN/SAC codes, bank details. Download PDF instantly. No sign-up needed."
        canonical="https://www.invilic.com/gst-invoice-generator"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="text-center mb-16">
          <span className="inline-block bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            GST Compliant · India
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            GST Invoice Generator
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Create GST-compliant tax invoices online in minutes. Auto-calculates CGST, SGST, and IGST. Add GSTIN, HSN/SAC codes, and bank details. Download a professional PDF for Free, no sign-up needed.
          </p>
          <Link
            to="/create/gst"
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-emerald-600/20 transition-all duration-200 text-lg"
          >
            Create GST Invoice Free <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">No credit card · No account required</p>
        </div>

        {/* What's included */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            What's Included in Your GST Invoice
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {fields.map(f => (
              <div key={f} className="flex items-center gap-3 p-4 rounded-xl border border-emerald-100 dark:border-emerald-800/30 bg-emerald-50 dark:bg-emerald-900/10">
                <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">{f}</span>
              </div>
            ))}
          </div>
        </section>

        {/* GST types explainer */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            CGST, SGST & IGST: Automatically Calculated
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6 leading-relaxed">
            Invilic handles all GST tax calculations for you. Just enter your HSN/SAC code and the applicable GST rate. The tool splits it correctly based on transaction type.
          </p>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { label: 'CGST + SGST', desc: 'For intra-state sales (within same state). Both are equal halves of the total GST rate.', color: 'emerald' },
              { label: 'IGST', desc: 'For inter-state sales (between different states). Applied as a single rate equal to CGST + SGST combined.', color: 'teal' },
              { label: 'Exempt / 0%', desc: 'For items that are GST-exempt or zero-rated. Invilic handles these too.', color: 'gray' },
            ].map(({ label, desc, color }) => (
              <div key={label} className={`p-5 rounded-xl border border-${color}-100 dark:border-${color}-800/30 bg-${color}-50 dark:bg-${color}-900/10`}>
                <div className={`font-bold text-${color}-700 dark:text-${color}-300 mb-2 flex items-center gap-2`}>
                  <Percent className="w-4 h-4" />{label}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Who uses it */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Who Uses Invilic for GST Invoicing?
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { icon: Building2, title: 'Small businesses & startups', desc: 'Issue GST-compliant tax invoices to B2B and B2C customers without expensive accounting software.' },
              { icon: FileText, title: 'Freelancers & consultants', desc: 'Bill clients with a proper GST invoice that includes your GSTIN and service description.' },
              { icon: Percent, title: 'Traders & manufacturers', desc: 'Generate invoices with HSN codes and proper CGST/SGST/IGST breakdowns for goods.' },
              { icon: Download, title: 'Anyone needing a GST invoice', desc: 'No accounting knowledge needed. Fill in the fields, download the PDF.' },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex gap-4 p-5 rounded-xl border border-gray-100 dark:border-gray-800">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white text-sm mb-1">{title}</div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            GST Invoice: Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Create Your GST Invoice Now for Free
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">GST-compliant PDF in under 2 minutes. No account needed.</p>
          <Link
            to="/create/gst"
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-emerald-600/20 transition-all duration-200 text-lg"
          >
            Create GST Invoice <ArrowRight className="w-5 h-5" />
          </Link>
        </section>
      </main>
    </div>
  );
}

